import { useCallback } from "react";
import { CellValueChangedEvent } from "ag-grid-community";
import { TQC1040_R01Dto } from "../Dto_TQC1040_R01";

interface UseTQC1040_R01CellChangeProps {
	updateTQC1040_R01List: (index: number, data: TQC1040_R01Dto) => void;
	originalTQC1040_R01List: TQC1040_R01Dto[];
}

/**
 * 텍스트 편집 셀(비고 등)의 값 변경을 스토어에 반영하는 훅.
 * (검사항목 Y/N 은 스위치 버튼 핸들러가 별도로 처리하므로 여기서는 관여하지 않는다.)
 *
 * @param updateTQC1040_R01List  변경된 행을 valueIndex 로 갱신하는 스토어 액션
 * @param originalTQC1040_R01List 조회 직후 원본 목록 (수정 여부 판정 기준)
 */
export function useTQC1040_R01CellChange({
	updateTQC1040_R01List,
	originalTQC1040_R01List,
}: UseTQC1040_R01CellChangeProps) {
	// 셀 값이 바뀌면 호출된다. 바뀐 필드를 반영한 뒤,
	// 편집 대상 필드 전부가 원본과 동일하면 isModified 를 해제해 불필요한 저장을 막는다.
	const handleCellChange = useCallback(
		(event: CellValueChangedEvent<TQC1040_R01Dto>) => {
			const { data, colDef, newValue } = event;
			if (!data || !colDef.field) return;
			const field = colDef.field as keyof TQC1040_R01Dto;

			const updatedData: TQC1040_R01Dto = {
				...data,
				[field]: newValue as any,
				isModified: true,
			};

			// 원본과 모든 편집 필드가 동일하면 수정 표시를 해제한다.
			const originalRow = originalTQC1040_R01List.find(
				(item) => item.valueIndex === data.valueIndex,
			);
			if (originalRow) {
				const editableFields: (keyof TQC1040_R01Dto)[] = [
					"inspectValue1", "inspectValue2", "inspectValue3",
					"inspectValue4", "inspectValue5", "inspectValue6",
					"inspectEmpno", "remark",
				];
				const unchanged = editableFields.every(
					(f) => originalRow[f] === updatedData[f],
				);
				if (unchanged) updatedData.isModified = false;
			}

			updateTQC1040_R01List(data.valueIndex, updatedData);
		},
		[updateTQC1040_R01List, originalTQC1040_R01List],
	);

	return { handleCellChange };
}
